import { Component } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { ReportDeliveryStatus } from 'ldt-data-deliveries-api';

interface DownloadDeliveryCellParams extends ICellRendererParams {
  onDownload?: (params: any) => void;
}

/**
 * Delivery cell for the downloads list. Deliveries sent to a configured destination show
 * the destination name (with a secondary option to download a copy); manual downloads
 * show the usual download button.
 */
@Component({
  selector: 'app-download-delivery-cell',
  template: `
    <div class="tw-flex tw-items-center tw-gap-1 tw-h-full">
      <ng-container *ngIf="destinationName; else downloadOnly">
        <span
          matTooltip="Delivered to {{ destinationName }}"
          class="tw-px-2 tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-leading-5 tw-font-semibold tw-rounded tw-bg-purple-100 tw-text-purple-800 dark:tw-bg-purple-900 dark:tw-text-purple-300 tw-max-w-40">
          <mat-icon class="tw-text-sm tw-w-4 tw-h-4 tw-leading-4 tw-shrink-0">outbound</mat-icon>
          <span class="tw-truncate">{{ destinationName }}</span>
        </span>
        <button
          matTooltip="Download a copy"
          class="tw-bg-transparent tw-border-0 tw-p-2 tw-rounded-full tw-leading-[0px]"
          [ngClass]="
            downloadDisabled
              ? 'tw-text-gray-400 dark:tw-text-gray-500'
              : 'tw-text-gray-600 dark:tw-text-gray-400 hover:tw-bg-gray-100 dark:hover:tw-bg-gray-900'
          "
          [disabled]="downloadDisabled"
          (click)="download()">
          <mat-icon>download</mat-icon>
        </button>
      </ng-container>
      <ng-template #downloadOnly>
        <button
          matTooltip="Download File (not available for all types)"
          class="tw-bg-transparent tw-border-0 tw-p-2 tw-rounded-full tw-leading-[0px]"
          [ngClass]="
            downloadDisabled
              ? 'tw-text-gray-400 dark:tw-text-gray-500'
              : 'tw-text-link-color-dynamic dark:hover:tw-bg-gray-900/20'
          "
          [disabled]="downloadDisabled"
          (click)="download()">
          <mat-icon>download</mat-icon>
        </button>
      </ng-template>
    </div>
  `,
  styles: [],
  standalone: false,
})
export class DownloadDeliveryCellComponent implements ICellRendererAngularComp {
  destinationName: string | null = null;
  downloadDisabled = true;
  private params: DownloadDeliveryCellParams;

  agInit(params: DownloadDeliveryCellParams): void {
    this.params = params;
    const data = params.data || {};
    const metadata = data.scheduled_report_metadata;

    this.destinationName = metadata?.destination_id
      ? metadata?.destination?.name || 'Destination'
      : null;

    const isComplete = data.status === ReportDeliveryStatus.Complete;
    const isOneTimeOrScheduled = data.type === 'one_time' || data.type === 'scheduled';
    this.downloadDisabled = !isComplete || !isOneTimeOrScheduled;
  }

  refresh(params: DownloadDeliveryCellParams): boolean {
    this.agInit(params);
    return true;
  }

  download(): void {
    if (this.downloadDisabled) {
      return;
    }
    if (this.params.onDownload) {
      this.params.onDownload({ rowData: this.params.data });
    }
  }
}
